import React, { ReactNode, useRef, useEffect } from 'react';
import { Overlay, useOverlayPosition } from '@react-aria/overlays';
import { Placement } from '@react-aria/overlays';
import {
  useTooltipTrigger,
  useTooltip,
  AriaTooltipProps,
  TooltipTriggerProps,
} from '@react-aria/tooltip';
import { mergeProps } from '@react-aria/utils';
import styles from '@danske/sapphire-css/components/tooltip/tooltip.module.css';
import { useTooltipTriggerState } from '@react-stately/tooltip';
import { FocusableRef } from '@react-types/shared';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { useFocusableRef } from '@react-spectrum/utils';
import clsx from 'clsx';
import { FocusableProvider } from '@react-aria/focus';
import { ThemeRoot } from '../../ThemeRoot';
import {
  BaseComponentKeys,
  GlobalDomAttributes,
  OverlayTriggerPropsKeys,
} from '../../utils/types';

type TooltipChildren =
  | React.ReactNode
  | ((
      props: React.HTMLAttributes<HTMLElement> & {
        ref: React.Ref<HTMLElement>;
      }
    ) => React.ReactNode);

export type TooltipProps = Pick<AriaTooltipProps, BaseComponentKeys> &
  Pick<
    TooltipTriggerProps,
    'trigger' | 'isDisabled' | OverlayTriggerPropsKeys
  > &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * Tooltip text.
     */
    title: ReactNode;
    /**
     * The trigger for the tooltip. It can be:
     * - A focusable Sapphire component (preferred)
     * - A native html element
     * - A function that accepts trigger props and renders any component,
     *   passing the trigger props.
     */
    children: TooltipChildren;
    /**
     * 	The placement of the tooltip element with respect to its anchor element.
     * @default 'bottom'
     */
    placement?: Placement;
    /**
     * Delay time for opening the tooltip (only on hover) in milliseconds. Keyboard focus will
     * always open it immediately for better accessibility. It can be either set to 0s or in a
     * range of 300 - 500ms.
     * @default 500
     */
    delay?: number;
    /**
     * The delay time for the tooltip to close
     */
    closeDelay?: number;
  };

export const Tooltip = React.forwardRef(function Tooltip(
  {
    title,
    children,
    placement = 'bottom',
    delay = 500,
    ...otherProps
  }: TooltipProps,
  ref: FocusableRef<HTMLDivElement>
) {
  useThemeCheck();

  const { styleProps } = useSapphireStyleProps(otherProps);
  const triggerRef = useFocusableRef<HTMLDivElement>(ref);
  const overlayRef = useRef<HTMLSpanElement>(null);

  const state = useTooltipTriggerState({
    ...otherProps,
    delay: delay === 0 || (delay >= 300 && delay <= 500) ? delay : 500,
  });

  const { triggerProps, tooltipProps: triggerTooltipProps } = useTooltipTrigger(
    otherProps,
    state,
    triggerRef
  );
  const { tooltipProps } = useTooltip(triggerTooltipProps, state);

  const { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement,
    offset: 6,
    isOpen: state.isOpen,
  });

  // The position of the tooltip is managed by `useOverlayPosition` but in case the content
  // of the tooltip changes while it is opened we have to manually update the position. It
  // does not do it on its own. `useOverlayPosition` does not know about our `title`.
  useEffect(() => {
    updatePosition();
  }, [title]);

  // If we set isDisabled-prop true when using Tooltip, useFocus will down the line set onBlur to
  // null which will overwrite any onBlur set directly on the child of the tooltip (the trigger):
  // https://github.com/adobe/react-spectrum/blob/main/packages/%40react-aria/interactions/src/useFocus.ts#L71
  // Thus we have to check if triggerProps.onBlur actually has a value before passing it to the
  // child component (the trigger). Below illustrates the scenarios: If we just pass
  // triggerProps.onBlur to this FocusableProvider as is <Tooltip isDisabled={true} />  &&
  // <FocusableProvider onBlur={null} /> (will be null):                            DOESN'T WORK
  // (child onBlur is overwritten and NOT called) <Tooltip isDisabled={false} /> &&
  // <FocusableProvider onBlur={triggerProps.onBlur} /> (will be a function)}:      WORKS (child
  // onBlur is chained and is called) If we pass triggerProps.onBlur only if it has a value
  // <Tooltip isDisabled={true} />  && <FocusableProvider ...{} /> (would've been null):                              WORKS (child onBlur is chained and is called) <Tooltip isDisabled={false} /> && <FocusableProvider ...{ onBlur: triggerProps.onBlur } /> (will be a function): WORKS (child onBlur is chained and is called) This is only an issue with onBlur as onFocus is never set to null down the line. We plan to open and issue within the Spectrum source code (linked above) for getting this fixed (or figuring out the reasoning for setting onBlur null and not just undefined).
  if ('onBlur' in triggerProps && !triggerProps.onBlur) {
    delete triggerProps.onBlur;
  }

  return (
    <>
      {normalizeTrigger(children, triggerProps, triggerRef)}
      {state.isOpen && (
        <Overlay>
          <ThemeRoot variant="contrast">
            <span
              role="tooltip"
              ref={overlayRef}
              className={styles['sapphire-tooltip']}
              {...mergeProps(positionProps, tooltipProps, triggerTooltipProps)}
            >
              <div
                className={clsx(
                  styles['sapphire-tooltip__content'],
                  styleProps.className
                )}
                style={styleProps.style}
              >
                {title}
              </div>
            </span>
          </ThemeRoot>
        </Overlay>
      )}
    </>
  );
});

function normalizeTrigger(
  trigger: TooltipChildren,
  // react-aria adds a tabIndex to all native triggers of tooltip
  // which breaks some of our usages in tables
  // https://github.com/adobe/react-spectrum/commit/cdba748760b1e30fb15414e8b585d05fd167819e#diff-b76f394c833099e095393426f9300b2f787a122b4149f952265b81632cbfab23
  { tabIndex, ...triggerProps }: React.HTMLAttributes<HTMLElement>,
  triggerRef: React.Ref<HTMLElement>
): React.ReactNode {
  if (React.isValidElement(trigger)) {
    // if the trigger is a native element
    if (typeof trigger.type === 'string') {
      return React.cloneElement(trigger, { ...triggerProps, ref: triggerRef });
    }
    // If trigger is not a native element, a component which uses `useFocusable` is assumed to be
    // rendered. Sapphire focusable components implement `useFocusable` and therefore can be used
    // as tooltip trigger by default. We also pass triggerProps via cloneElement to support RAC-based
    // components (like Checkbox) that don't consume FocusableProvider context - they need the props
    // directly on the element. If a custom component or a non-focusable Sapphire component is to be
    // the trigger, a render function should be passed as the children of the Tooltip, which accepts
    // the props and ref, and applies it on the trigger as needed.
    return (
      <FocusableProvider {...triggerProps} ref={triggerRef}>
        {React.cloneElement(trigger, mergeProps(trigger.props, triggerProps))}
      </FocusableProvider>
    );
  }
  if (typeof trigger === 'function') {
    return trigger({ ...triggerProps, ref: triggerRef });
  }
  // if a React node like a string or a number is passed, wrap it with a span to apply trigger props
  return (
    <span {...triggerProps} ref={triggerRef}>
      {trigger}
    </span>
  );
}
